import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard, { StatusBadge } from "../components/ProductCard";
import { Shield, Zap, Clock, CreditCard, ChevronRight, Activity } from "lucide-react";

const FEATURES = [
  {
    icon: Shield,
    title: "Undetected",
    description: "Our tools use advanced techniques to stay under the radar"
  },
  {
    icon: Zap,
    title: "Discord Delivery",
    description: "After purchase, join our Discord and open a ticket to receive your key."
  },
  {
    icon: Clock,
    title: "24/7 Support",
    description: "Our team is available around the clock on Discord"
  },
  {
    icon: CreditCard,
    title: "Crypto Payments",
    description: "Pay securely with cryptocurrency for privacy"
  }
];

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-[#0a0a12] border border-white/5 p-6 hover:border-[#4F7CFF]/30 transition-colors">
    <Icon className="w-8 h-8 text-[#4F7CFF] mb-4" />
    <h3 className="font-heading text-lg text-white mb-2">{title}</h3>
    <p className="text-[#A1A1AA] text-sm">{description}</p>
  </div>
);

const StatusOverviewItem = ({ item }) => (
  <div className="bg-[#0a0a12] border border-white/5 p-4 flex items-center justify-between">
    <div>
      <p className="text-[#A1A1AA] text-xs uppercase tracking-wider">{item?.game || "System"}</p>
      <p className="text-white font-medium">{item?.name || "Unknown"}</p>
    </div>
    <StatusBadge status={item?.status || "updating"} />
  </div>
);

const LoadingGrid = ({ count = 3 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }, (_, i) => (
      <div key={`loading-${i}`} className="bg-[#0a0a12] border border-white/5 h-80 animate-pulse" />
    ))}
  </div>
);

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [statusOverview, setStatusOverview] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      if (!API) {
        setProducts([]);
        setStatusOverview([]);
        return;
      }

      const [productsRes, statusRes] = await Promise.all([
        axios.get(`${API}/products`).catch(() => ({ data: [] })),
        axios.get(`${API}/status-overview`).catch(() => ({ data: [] }))
      ]);

      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setStatusOverview(Array.isArray(statusRes.data) ? statusRes.data : []);
    } catch {
      setProducts([]);
      setStatusOverview([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="home-page">
      <Navbar />

      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4F7CFF]/10 via-[#050508] to-[#050508]" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#4F7CFF]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#4F7CFF]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#4F7CFF]/30 bg-[#4F7CFF]/10 mb-8 animate-fade-in-up">
            <span className="w-2 h-2 rounded-full bg-[#00FF66] status-pulse" />
            <span className="text-[#4F7CFF] text-sm uppercase tracking-wider font-mono">Systems Online</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white mb-6 animate-fade-in-up animation-delay-100">
            NEXUS<span className="text-[#4F7CFF]">FORGE</span>
          </h1>

          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200">
            Premium products with Discord-based delivery, live status updates, and reliable support.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
            <Link
              to="/products"
              className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wider"
              data-testid="browse-products-btn"
            >
              Browse Products
              <ChevronRight className="w-4 h-4" />
            </Link>
            <a
              href="https://discord.gg/efeySGKGJk"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2 text-sm uppercase tracking-wider"
              data-testid="join-discord-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Join Discord
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-white/20 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#050508]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#08080e]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl text-white">Featured Products</h2>
            <Link
              to="/products"
              className="text-[#4F7CFF] text-sm uppercase tracking-wider flex items-center gap-1 hover:underline"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <LoadingGrid count={3} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(Array.isArray(products) ? products : []).slice(0, 6).map((product) => (
                <div key={product.id} className="animate-fade-in-up">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-[#050508] relative" data-testid="status-overview-section">
        <div className="absolute inset-0 scanlines opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl text-white flex items-center gap-3">
              <Activity className="w-6 h-6 text-[#4F7CFF]" />
              Status Overview
            </h2>
            <Link
              to="/status"
              className="text-[#4F7CFF] text-sm uppercase tracking-wider flex items-center gap-1 hover:underline"
            >
              Full Status
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(Array.isArray(statusOverview) ? statusOverview : []).slice(0, 6).map((item) => (
              <StatusOverviewItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#08080e]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">
            JOIN OUR <span className="text-[#4F7CFF]">COMMUNITY</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8">
            Get support, updates, and connect with other users on our Discord server.
          </p>
          <a
            href="https://discord.gg/efeySGKGJk"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider"
            data-testid="cta-discord-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join Discord Server
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
